import { useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';
import { AdjustStockModal } from './AdjustStockModal.jsx';
import { EditItemModal } from './EditItemModal.jsx';
import { LinkPrinterModal } from './LinkPrinterModal.jsx';
import { SupplierInfoModal } from './SupplierInfoModal.jsx';

const YELLOW = '#F4C430';

const STATUS_META = {
  low:    { label: 'Low',           tone: 'text-ink-magenta', dot: '#E63B7A' },
  medium: { label: 'Medium',        tone: 'text-ink-yellow',  dot: '#F4C430' },
  high:   { label: 'High',          tone: 'text-status-ok',   dot: '#7BD4A8' },
  unset:  { label: 'Needs thresholds', tone: 'text-text-tertiary', dot: '#A8A095' },
};

const formatRelative = (iso) => {
  const dt = new Date(iso);
  const diff = (Date.now() - dt.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return dt.toLocaleDateString('en-AU');
};

export const InventoryItemDetailPage = ({ id }) => {
  const { route } = useLocation();
  const { data: item, loading, error, refetch } = useApi(`/inventory/${id}`);
  const { data: log, refetch: refetchLog } = useApi(`/inventory/${id}/log`);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [low, setLow] = useState('');
  const [med, setMed] = useState('');
  const [editingThresholds, setEditingThresholds] = useState(false);
  const [savingThresholds, setSavingThresholds] = useState(false);
  const [busy, setBusy] = useState(false);

  const openEditThresholds = () => {
    setLow(item.lowThreshold == null ? '' : String(item.lowThreshold));
    setMed(item.medThreshold == null ? '' : String(item.medThreshold));
    setEditingThresholds(true);
  };

  const saveThresholds = async () => {
    setSavingThresholds(true);
    try {
      await api.patch(`/inventory/${item.id}`, {
        lowThreshold: low === '' ? null : parseInt(low, 10),
        medThreshold: med === '' ? null : parseInt(med, 10),
      });
      setEditingThresholds(false);
      refetch();
    } catch (err) {
      alert(err.message ?? 'Failed to save thresholds');
    } finally {
      setSavingThresholds(false);
    }
  };

  const archive = async () => {
    if (!window.confirm(`Archive "${item.name}"? It'll be hidden from the index but the log history stays.`)) return;
    setBusy(true);
    try {
      await api.patch(`/inventory/${item.id}`, { status: 'archived' });
      route('/inventory');
    } catch (err) {
      alert(err.message ?? 'Failed to archive');
      setBusy(false);
    }
  };

  const unlinkPrinter = async (printerId) => {
    if (item.linkedPrinters.length <= 1) {
      alert("Can't unlink the only printer — delete the item or link another printer first.");
      return;
    }
    if (!window.confirm(`Unlink "${item.name}" from this printer? The stock stays with the remaining printer(s).`)) return;
    try {
      await api.delete(`/inventory/${item.id}/link-printer/${printerId}`);
      refetch();
    } catch (err) {
      alert(err.message ?? 'Failed to unlink');
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${item.name}"? The full log history will be deleted too.`)) return;
    setBusy(true);
    try {
      await api.delete(`/inventory/${item.id}`);
      route('/inventory');
    } catch (err) {
      alert(err.message ?? 'Failed to delete');
      setBusy(false);
    }
  };

  if (loading) return <p class="text-sm text-text-tertiary">Loading…</p>;
  if (error) return <p class="text-sm font-semibold text-ink-magenta">Failed to load: {error.message}</p>;
  if (!item) return null;

  const status = STATUS_META[item.stockStatus] ?? STATUS_META.unset;

  return (
    <div>
      <a href="/inventory" class="mb-3 inline-block text-[12px] font-semibold uppercase tracking-eyebrow text-text-tertiary hover:text-text-primary">
        ← Inventory
      </a>

      <section class="mb-6 flex items-start justify-between gap-4">
        <div>
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: YELLOW }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: YELLOW }}>
              {item.linkedPrinters.length === 0 ? 'Global' : item.linkedPrinters.map((p) => p.name).join(' · ')} · {item.category}
            </span>
          </div>
          <h1 class="m-0 mb-1 text-[30px] leading-[1.18] tracking-tight text-text-primary">{item.name}</h1>
          {item.brandName && <p class="m-0 text-sm text-text-secondary">{item.brandName}</p>}
        </div>
        <div class="flex shrink-0 gap-2">
          <Button variant="primary" onClick={() => setAdjustOpen(true)}>± Adjust stock</Button>
          {isAdmin && (
            <>
              <Button variant="secondary" onClick={() => setEditOpen(true)}>Edit</Button>
              <Button variant="ghost" onClick={archive} disabled={busy} class="text-text-tertiary">Archive</Button>
            </>
          )}
        </div>
      </section>

      {/* Hero stock */}
      <div class="mb-6 rounded-lg border border-border-subtle bg-bg-card p-5">
        <div class="flex items-end justify-between gap-4">
          <div>
            <p class="m-0 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">In stock</p>
            <p class="m-0 mt-1 font-serif text-[52px] font-medium leading-none tracking-tighter text-text-primary">
              {item.stockCount}
            </p>
          </div>
          <div class="flex items-center gap-3">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: status.dot }} />
            <span class={`font-semibold ${status.tone}`}>{status.label}</span>
          </div>
        </div>

        <div class="mt-5 flex flex-wrap items-end gap-3 border-t border-border-subtle pt-4">
          {editingThresholds ? (
            <>
              <div>
                <p class="mb-1 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Low ≤</p>
                <input type="number" min={0} value={low} onInput={(e) => setLow(e.currentTarget.value)} class="block w-24 rounded-md border border-border-strong bg-bg-base px-2 py-1.5 text-right font-serif text-[16px] font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
              </div>
              <div>
                <p class="mb-1 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Medium ≤</p>
                <input type="number" min={0} value={med} onInput={(e) => setMed(e.currentTarget.value)} class="block w-24 rounded-md border border-border-strong bg-bg-base px-2 py-1.5 text-right font-serif text-[16px] font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
              </div>
              <Button variant="primary" onClick={saveThresholds} disabled={savingThresholds}>{savingThresholds ? 'Saving…' : 'Save'}</Button>
              <Button variant="secondary" onClick={() => setEditingThresholds(false)} disabled={savingThresholds}>Cancel</Button>
            </>
          ) : (
            <>
              <div class="flex gap-6">
                <div>
                  <p class="m-0 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Low ≤</p>
                  <p class="m-0 mt-0.5 font-serif text-[20px] font-medium text-text-primary">
                    {item.lowThreshold ?? <span class="text-text-muted">—</span>}
                  </p>
                </div>
                <div>
                  <p class="m-0 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Medium ≤</p>
                  <p class="m-0 mt-0.5 font-serif text-[20px] font-medium text-text-primary">
                    {item.medThreshold ?? <span class="text-text-muted">—</span>}
                  </p>
                </div>
                <div>
                  <p class="m-0 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">High &gt;</p>
                  <p class="m-0 mt-0.5 font-serif text-[20px] font-medium text-text-primary">
                    {item.medThreshold ?? <span class="text-text-muted">—</span>}
                  </p>
                </div>
              </div>
              {isAdmin && (
                <Button variant="secondary" onClick={openEditThresholds} class="ml-auto">Edit thresholds</Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Used by — shared printer list */}
      <div class="mb-6 rounded-lg border border-border-subtle bg-bg-card p-4">
        <div class="mb-2 flex items-center justify-between">
          <p class="m-0 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">
            Used by {item.linkedPrinters.length > 1 && <span class="ml-1 font-bold text-ink-yellow">· shared</span>}
          </p>
          {isAdmin && (
            <button onClick={() => setLinkOpen(true)} class="text-[12px] font-semibold text-brand hover:underline">
              + Link printer
            </button>
          )}
        </div>
        {item.linkedPrinters.length === 0 ? (
          <p class="m-0 text-[13px] text-text-tertiary">
            Global — shared across all printers.
          </p>
        ) : (
          <div class="flex flex-wrap gap-1.5">
            {item.linkedPrinters.map((p) => (
              <span
                key={p.id}
                class="inline-flex items-center gap-1.5 rounded-full border border-border-strong bg-bg-base px-2.5 py-1 text-[12px] font-semibold text-text-body"
              >
                <a href={`/printers/${p.id}`} class="hover:text-text-primary">{p.name}</a>
                {isAdmin && item.linkedPrinters.length > 1 && (
                  <button
                    type="button"
                    onClick={() => unlinkPrinter(p.id)}
                    title={`Unlink from ${p.name}`}
                    class="ml-1 text-text-muted hover:text-ink-magenta"
                  >
                    ✕
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
        {item.linkedPrinters.length > 1 && (
          <p class="m-0 mt-2 text-[11px] font-medium text-text-muted">
            Stock is shared — adjustments here apply to all linked printers.
          </p>
        )}
      </div>

      {/* Identification + Supplier */}
      <div class="mb-6 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div class="rounded-lg border border-border-subtle bg-bg-card p-4">
          <p class="m-0 mb-2 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Identification</p>
          <table class="w-full text-[13px]">
            <tbody>
              <tr><td class="w-1/3 py-1 text-text-tertiary">Barcode</td><td class="py-1 font-mono text-text-body">{item.barcode || <span class="text-text-muted">—</span>}</td></tr>
              <tr><td class="py-1 text-text-tertiary">Product ID</td><td class="py-1 font-mono text-text-body">{item.productId || <span class="text-text-muted">—</span>}</td></tr>
              <tr><td class="py-1 text-text-tertiary">Category</td><td class="py-1 capitalize text-text-body">{item.category}</td></tr>
            </tbody>
          </table>
        </div>
        <div class="rounded-lg border border-border-subtle bg-bg-card p-4">
          <div class="mb-2 flex items-center justify-between">
            <p class="m-0 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Supplier &amp; reorder</p>
            <button onClick={() => setSupplierOpen(true)} class="text-[12px] font-semibold text-brand hover:underline">Edit</button>
          </div>
          <table class="w-full text-[13px]">
            <tbody>
              <tr><td class="w-2/5 py-1 text-text-tertiary">Supplier</td><td class="py-1 text-text-body">{item.supplierName || <span class="text-text-muted">—</span>}</td></tr>
              <tr><td class="py-1 text-text-tertiary">Brand</td><td class="py-1 text-text-body">{item.brandName || <span class="text-text-muted">—</span>}</td></tr>
              <tr><td class="py-1 text-text-tertiary">Reorder qty</td><td class="py-1 font-serif text-text-body">{item.reorderQty ?? <span class="text-text-muted">—</span>}</td></tr>
              <tr><td class="py-1 text-text-tertiary">Last reorder</td><td class="py-1 text-text-body">{item.lastReorderAt ?? <span class="text-text-muted">—</span>}</td></tr>
            </tbody>
          </table>
          {item.batchNotes && (
            <p class="m-0 mt-2 border-t border-border-subtle pt-2 text-[12px] font-medium text-text-secondary whitespace-pre-wrap">
              {item.batchNotes}
            </p>
          )}
        </div>
      </div>

      {/* History */}
      <div class="mb-6 rounded-lg border border-border-subtle bg-bg-card overflow-hidden">
        <div class="border-b border-border-subtle px-4 py-2.5">
          <p class="m-0 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">
            Stock log {log && log.length > 0 && <span class="font-medium normal-case text-text-muted">· {log.length} entries</span>}
          </p>
        </div>
        {log && log.length > 0 ? log.map((entry) => (
          <div key={entry.id} class="flex items-start gap-3 border-b border-border-subtle px-4 py-2.5 last:border-b-0">
            <span
              class={`font-serif text-[18px] font-medium tabular-nums ${entry.changeAmount < 0 ? 'text-ink-magenta' : 'text-status-ok'}`}
              style={{ minWidth: '3rem', textAlign: 'right' }}
            >
              {entry.changeAmount > 0 ? '+' : ''}{entry.changeAmount}
            </span>
            <div class="min-w-0 flex-1">
              <p class="m-0 text-[13px] font-medium text-text-body">
                {entry.reason || <span class="text-text-muted">Quick adjustment</span>}
              </p>
              <p class="m-0 text-[11px] font-medium text-text-tertiary">
                {entry.userName ?? 'Unknown'} · {formatRelative(entry.createdAt)}
              </p>
            </div>
          </div>
        )) : (
          <p class="m-0 px-4 py-3 text-[13px] text-text-tertiary">No adjustments yet.</p>
        )}
      </div>

      {item.notes && (
        <div class="mb-6 rounded-lg border border-border-subtle bg-bg-card p-4">
          <p class="m-0 mb-1 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">Notes</p>
          <p class="m-0 whitespace-pre-wrap text-[13px] font-medium text-text-body">{item.notes}</p>
        </div>
      )}

      {isAdmin && (
        <div class="flex justify-end">
          <Button variant="ghost" onClick={remove} disabled={busy} class="text-ink-magenta hover:text-ink-magenta">
            Delete item
          </Button>
        </div>
      )}

      <AdjustStockModal
        open={adjustOpen}
        item={item}
        onClose={() => setAdjustOpen(false)}
        onAdjusted={() => { setAdjustOpen(false); refetch(); refetchLog(); }}
      />
      <SupplierInfoModal
        open={supplierOpen}
        item={item}
        onClose={() => setSupplierOpen(false)}
        onSaved={() => { setSupplierOpen(false); refetch(); }}
      />
      <LinkPrinterModal
        open={linkOpen}
        item={item}
        onClose={() => setLinkOpen(false)}
        onLinked={() => { setLinkOpen(false); refetch(); refetchLog(); }}
      />
      <EditItemModal
        open={editOpen}
        item={item}
        onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); refetch(); }}
      />
    </div>
  );
};
